import { calculateEarnings } from '@util/firestore/orders';
import { PurchaseEntryDocument } from '@util/types/firestore/purchase_entry';
import { getProductSlug } from '@util/urlHelpers';
import { OrderDocument, OrderItemDocument } from 'models/order';
import { ProductDocument } from 'models/product';
import { UserDocument } from 'models/user';

export function generatePurchaseEntryProductInfo(
  product: ProductDocument,
  orderItem: OrderItemDocument | null,
  orderDoc: OrderDocument | null,
  userDoc?: UserDocument | null
): PurchaseEntryProductInfo {
  let net = 0;
  if (orderItem) {
    [net] = calculateEarnings({
      item: orderItem,
      item_count: orderDoc?.item_count ?? 1,
      sellerTaxStates: userDoc?.tax_states,
      fee: userDoc?.fee,
      created: orderDoc?.created ?? 0,
    });
    // Flat Rate shipping so subtract what it cost the seller to ship -
    if (!orderItem.rate_id && orderItem.seller_shipping_cost) {
      net -= orderItem.seller_shipping_cost ?? 0;
      net += orderItem.buyer_shipping_cost ?? 0;
    }
    net = Math.max(0, net);
  }

  const tableRow: PurchaseEntryProductInfo = {
    id: product.id,
    icon: product.thumbnail,
    name: product.title,
    url: getProductSlug(product),
    boughtPrice: product.purchase_entry_price ?? 0,
    listedPrice: product.price,
    status: getProductState(product, orderItem),
    ...(!!orderItem
      ? {
          orderNum: (orderItem as any)?.orderNum,
          orderId: (orderItem as any)?.orderId,
          soldDate: (orderItem as any)?.soldDate,
          netProfit: net - (product.purchase_entry_price ?? 0),
          netEarnings: net,
          shippingPrice:
            orderItem.seller_shipping_cost ??
            orderItem.buyer_shipping_cost ??
            0,
          buyerShippingCost: orderItem.buyer_shipping_cost ?? 0,
          sellerShippingCost: orderItem.seller_shipping_cost ?? 0,
          soldPrice:
            orderItem.product_cost +
            (orderItem.buyer_shipping_cost ?? 0) +
            (orderItem.product_tax ?? 0), // otherwise would have been * qty
        }
      : {}),
  };
  if (product.purchase_entry_id === BOOT_TRADE_IN_PURCHASE_ENTRY_ID) {
    tableRow.tradedInFrom = product.sources?.find(
      (source) => source.collection === 'boot_trade_in'
    )?.id;
  }
  return tableRow;
}

export function getProductState(
  product: ProductDocument,
  orderItem?: OrderItemDocument | null
): ProductStatus {
  let state = null;

  if (product.out_of_stock && !product.is_draft) {
    state = 'sold' as const;
  }
  if (product.out_of_stock === false) {
    state = 'listed' as const;
  }
  if (product.date_added === undefined) {
    state = 'deleted' as const;
  }
  if (orderItem?.state === 5) {
    state = 'refunded' as const;
  }
  if (orderItem?.state === 7) {
    state = 'on_hold' as const;
  }

  return state;
}

export function paymentMethodToName(
  paymentMethod: PurchaseEntryDocument['payment_method']
) {
  const dictionary: { [key: string]: string } = {
    'motorsportlocker-balance': 'Motorsport Locker Balance',
    card: 'Card',
    bank: 'Bank',
    paypal: 'PayPal',
    venmo: 'Venmo',
  };

  const output = dictionary[paymentMethod];

  if (output === undefined) {
    throw new Error(`No mapping found for input: ${paymentMethod}`);
  }

  return output;
}

export type ProductStatus =
  | 'sold'
  | 'listed'
  | 'deleted'
  | 'refunded'
  | 'on_hold'
  | null;

export type PurchaseEntryProductInfo = {
  // From product info
  id: string;
  icon: string;
  name: string;
  url: string;
  boughtPrice: number;
  listedPrice: number;
  status: ProductStatus; // Listed === On Sale (in wireframe)
  tradedInFrom?: string;
  // From order info
  shippingPrice?: number;
  buyerShippingCost?: number;
  sellerShippingCost?: number;
  soldPrice?: number;
  orderNum?: number;
  orderId?: string;
  netProfit?: number;
  soldDate?: number;
  netEarnings?: number;
};

export const BOOT_TRADE_IN_PURCHASE_ENTRY_ID = process.env
  .NEXT_PUBLIC_IS_STAGING
  ? 'MXSC000054'
  : 'MXSC000385';
